import {NavList} from '@primer/react'
import {clsx} from 'clsx'
import {Suspense, useMemo} from 'react'
import type {PreloadedQuery} from 'react-relay/hooks'

import {LABELS} from '../../constants/labels'
import {VALUES} from '../../constants/values'
import {useNavigationContext} from '../../contexts/NavigationContext'
import useKnownViews from '../../hooks/use-known-views'
import {useViewsNav} from '../../hooks/viewsNav'
import {CallToActionItem} from '../CallToActionItem'
import type {SavedViewsQuery} from './__generated__/SavedViewsQuery.graphql'
import {AppTitle} from './AppTitle'
import {iconToPrimerIcon} from './IconHelper'
import {SavedViewItem} from './SavedViewItem'
import {SavedViews} from './SavedViews'
import styles from './Sidebar.module.css'
import {SidebarLoading} from './SidebarLoading'
import {SidebarRow} from './SidebarRow'

type SidebarProps = {
  customViewsRef: PreloadedQuery<SavedViewsQuery> | undefined | null
  isFullHeight?: boolean
}

export const Sidebar = ({customViewsRef, isFullHeight}: SidebarProps) => {
  if (!customViewsRef) return <SidebarLoading />
  return (
    <Suspense fallback={<SidebarLoading />}>
      <SidebarInternal customViewsRef={customViewsRef} isFullHeight={isFullHeight} />
    </Suspense>
  )
}

type SidebarInternalProps = {
  customViewsRef: PreloadedQuery<SavedViewsQuery>
  isFullHeight?: boolean
}

function SidebarInternal({customViewsRef, isFullHeight}: SidebarInternalProps) {
  const {knownViews, knownViewRoutes} = useKnownViews()
  const {isNavigationOpen} = useNavigationContext()

  const viewKeys = useMemo(() => knownViews.map((_item, index) => (index + 1).toString()), [knownViews])
  useViewsNav(knownViewRoutes, viewKeys)

  const savedViewItems = knownViews
    .filter(view => !view.hidden)
    .map((item, index) => {
      if (item.url) {
        return (
          <SidebarRow
            key={item.id}
            title={item.name}
            icon={iconToPrimerIcon(item.icon)!}
            id={item.id}
            path={item.url}
            tooltip={item.name}
          />
        )
      }

      return (
        <SavedViewItem
          key={item.id}
          id={item.id}
          position={index + 1}
          icon={item.icon}
          color={VALUES.defaultViewColor}
          title={item.name}
        />
      )
    })

  const Container = isNavigationOpen ? 'div' : 'nav'

  return (
    <Container
      aria-labelledby="sidebar-title"
      className={clsx(styles.SidebarContainer, isFullHeight && styles.SidebarContainerFullHeight)}
    >
      <div className="sr-only" aria-hidden="true">
        <AppTitle />
      </div>
      <NavList aria-label={LABELS.defaultViews} className={styles.SavedViewsList}>
        {savedViewItems}
      </NavList>
      <SavedViews savedViewsRef={customViewsRef} />
      <div className={styles.sidebarFooter}>
        <CallToActionItem />
      </div>
    </Container>
  )
}
